import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, Download } from 'lucide-react'
import { getAllDocuments, getDatasets, downloadDocument } from '../api/storage'
import { SearchInput } from '../components/SearchInput'
import { EmptyState } from '../components/EmptyState'
import { Modal } from '../components/Modal'
import { useDateFmt } from '../hooks/useDateFmt'
import { Button } from '../components/ui/button'
import type { StorageDocument, Dataset } from '../types'

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1_048_576).toFixed(1)} MB`
}


export function Documents() {
  const fmt = useDateFmt()
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['all-documents'],
    queryFn: () => getAllDocuments().then((r) => r.data),
  })

  const { data: datasets = [] } = useQuery({
    queryKey: ['datasets'],
    queryFn: () => getDatasets().then((r) => r.data),
  })

  const datasetMap = Object.fromEntries((datasets as Dataset[]).map((d) => [d.id, d.title]))

  const [search, setSearch] = useState('')
  const filtered = (documents as StorageDocument[]).filter((d) =>
    [d.title, d.mimeType, d.description ?? '', datasetMap[d.datasetId] ?? ''].some((v) =>
      v.toLowerCase().includes(search.toLowerCase())
    )
  )

  const [selected, setSelected] = useState<StorageDocument | null>(null)

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Documents</h1>
          <p className="text-sm text-ink-dim mt-0.5">All documents across every dataset</p>
        </div>
        <SearchInput value={search} onChange={setSearch} placeholder="Search documents…" />
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading…</div>
        ) : documents.length === 0 ? (
          <EmptyState icon={FileText} title="No documents yet" description="Upload documents through a dataset to see them here" />
        ) : filtered.length === 0 ? (
          <EmptyState icon={FileText} title="No results" description={`No documents match "${search}"`} />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th className="hidden md:table-cell">Dataset</th>
                <th className="hidden lg:table-cell">Type</th>
                <th className="hidden lg:table-cell">Size</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => setSelected(d)}
                  className="cursor-pointer"
                >
                  <td className="font-medium text-ink">{d.title}</td>
                  <td className="text-ink-dim hidden md:table-cell">
                    {datasetMap[d.datasetId] ?? <span className="text-ink-dim">—</span>}
                  </td>
                  <td className="font-mono text-xs text-ink-dim hidden lg:table-cell">{d.mimeType}</td>
                  <td className="text-ink-dim hidden lg:table-cell">{fmtSize(d.fileSize)}</td>
                  <td className="text-ink-dim">{fmt(d.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} size="md">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-0.5">Dataset</p>
                <p className="text-sm text-ink">{datasetMap[selected.datasetId] ?? <span className="text-ink-dim">—</span>}</p>
              </div>
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-0.5">Type</p>
                <p className="text-sm font-mono text-ink">{selected.mimeType}</p>
              </div>
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-0.5">Size</p>
                <p className="text-sm text-ink">{fmtSize(selected.fileSize)}</p>
              </div>
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-0.5">Created</p>
                <p className="text-sm text-ink">{fmt(selected.createdAt)}</p>
              </div>
            </div>

            {selected.metadata && Object.keys(selected.metadata).length > 0 && (
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">Metadata</p>
                <pre className="bg-paper-sunken border border-paper-border rounded-md px-4 py-3 text-xs font-mono text-ink overflow-x-auto max-h-64 scrollbar-thin">
                  {JSON.stringify(selected.metadata, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button
                icon={Download}
                onClick={() => { downloadDocument(selected.id, selected.title); setSelected(null) }}
              >
                Download
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
